/**
 * useSubscryptsContract - Hook for interacting with the Subscrypts smart contract.
 *
 * Provides type-safe methods for:
 * - Reading plan and subscription data
 * - Creating subscriptions
 * - Managing recurring payments
 * - DEX operations (quotes, swaps)
 * - Token operations (balances, approvals)
 */

import { useCallback, useMemo } from 'react';
import { formatUnits, parseUnits, type TransactionResponse, type TransactionReceipt } from 'ethers';
import { useWeb3 } from '../contexts/Web3Context';

// ============================================================================
// Types
// ============================================================================

export interface Plan {
  id: bigint;
  merchantAddress: string;
  currencyCode: bigint;      // 0 = SUBS, 1 = USDC
  subscriptionAmount: bigint;
  paymentFrequency: bigint;
  referralBonus: bigint;
  commission: bigint;
  description: string;
  defaultAttributes: string;
  verificationExpiryDate: bigint;
  isActive: boolean;
  subscriberCount: bigint;
}

export interface Subscription {
  id: bigint;
  planId: bigint;
  subscriber: string;
  referral: string;
  recurring: boolean;
  remainingCycles: bigint;
  lastPaymentDate: bigint;
  expiryDate: bigint;
  attribute: string;
  isActive: boolean;
}

export interface ContractDetails {
  name: string;
  symbol: string;
  decimals: number;
  totalSupply: bigint;
  contractAddress: string;
  owner: string;
}

// ============================================================================
// Hook
// ============================================================================

export const useSubscryptsContract = () => {
  const { getContract, getAbi, contractAddress, signer, isConnected, abis } = useWeb3();

  // ============================================================================
  // Contract Instances
  // ============================================================================

  const subsContract = useMemo(() => {
    if (!abis.combinedABI) return null;
    try {
      return getContract(contractAddress, getAbi('combinedABI'), 'auto');
    } catch {
      return null;
    }
  }, [getContract, getAbi, contractAddress, abis]);

  const subsReadOnly = useMemo(() => {
    if (!abis.combinedABI) return null;
    try {
      return getContract(contractAddress, getAbi('combinedABI'), 'readOnly');
    } catch {
      return null;
    }
  }, [getContract, getAbi, contractAddress, abis]);

  const subsSigner = useMemo(() => {
    if (!abis.combinedABI || !signer) return null;
    try {
      return getContract(contractAddress, getAbi('combinedABI'), 'signer');
    } catch {
      return null;
    }
  }, [getContract, getAbi, contractAddress, signer, abis]);

  // ============================================================================
  // Helper: Wait for transaction receipt
  // ============================================================================

  const waitForReceipt = useCallback(async (tx: TransactionResponse): Promise<TransactionReceipt> => {
    const receipt = await tx.wait();
    if (!receipt) {
      throw new Error('Transaction failed - no receipt');
    }
    return receipt;
  }, []);

  // ============================================================================
  // READ METHODS
  // ============================================================================

  /**
   * Get plan details by ID
   */
  const getPlan = useCallback(async (planId: number | bigint): Promise<Plan | null> => {
    if (!subsReadOnly) return null;
    try {
      const plan = await subsReadOnly.getPlan(planId);
      return plan;
    } catch (e) {
      console.error('Failed to get plan:', e);
      return null;
    }
  }, [subsReadOnly]);

  /**
   * Get multiple plans
   */
  const getPlans = useCallback(async (indexStart: number, indexEnd: number): Promise<Plan[]> => {
    if (!subsReadOnly) return [];
    try {
      return await subsReadOnly.getPlans(indexStart, indexEnd);
    } catch (e) {
      console.error('Failed to get plans:', e);
      return [];
    }
  }, [subsReadOnly]);

  /**
   * Get subscription by ID
   */
  const getSubscription = useCallback(async (subscriptionId: number | bigint): Promise<Subscription | null> => {
    if (!subsReadOnly) return null;
    try {
      return await subsReadOnly.getSubscription(subscriptionId);
    } catch (e) {
      console.error('Failed to get subscription:', e);
      return null;
    }
  }, [subsReadOnly]);

  /**
   * Get subscription for a plan and subscriber
   */
  const getPlanSubscription = useCallback(async (planId: number | bigint, subscriber: string): Promise<Subscription | null> => {
    if (!subsReadOnly) return null;
    try {
      return await subsReadOnly.getPlanSubscription(planId, subscriber);
    } catch (e) {
      console.error('Failed to get plan subscription:', e);
      return null;
    }
  }, [subsReadOnly]);

  /**
   * Get SUBS token balance
   */
  const balanceOf = useCallback(async (address: string): Promise<bigint> => {
    if (!subsReadOnly) return 0n;
    try {
      return await subsReadOnly.balanceOf(address);
    } catch (e) {
      console.error('Failed to get balance:', e);
      return 0n;
    }
  }, [subsReadOnly]);

  /**
   * Get token allowance
   */
  const allowance = useCallback(async (owner: string, spender: string): Promise<bigint> => {
    if (!subsReadOnly) return 0n;
    try {
      return await subsReadOnly.allowance(owner, spender);
    } catch (e) {
      console.error('Failed to get allowance:', e);
      return 0n;
    }
  }, [subsReadOnly]);

  /**
   * Get plan creation cost (in ETH)
   */
  const planCreationCost = useCallback(async (): Promise<bigint> => {
    if (!subsReadOnly) return 0n;
    try {
      return await subsReadOnly.planCreationCost();
    } catch (e) {
      console.error('Failed to get plan creation cost:', e);
      return 0n;
    }
  }, [subsReadOnly]);

  /**
   * Convert SUBS amount to USD value
   */
  const convertTokenToOtherCurrency = useCallback(async (amount: bigint): Promise<bigint> => {
    if (!subsReadOnly) return 0n;
    try {
      const result = await subsReadOnly.convertTokenToOtherCurrency(amount);
      // Contract returns [outputUSDC, inputSUBS, rate]
      return result[0];
    } catch (e: any) {
      // Expected revert on low-liquidity pools — not a real error
      const msg = e?.reason || e?.message || '';
      if (msg.includes('too small') || msg.includes('CALL_EXCEPTION')) {
        console.debug('USD conversion unavailable (low liquidity):', msg);
      } else {
        console.error('Failed to convert token to USD:', e);
      }
      return 0n;
    }
  }, [subsReadOnly]);

  /**
   * Get DEX addresses
   */
  const getDexAddresses = useCallback(async () => {
    if (!subsReadOnly) return null;
    try {
      const [router, quoter, usdc, pair] = await Promise.all([
        subsReadOnly.dexRouter(),
        subsReadOnly.dexQuoter(),
        subsReadOnly.dexUSDC(),
        subsReadOnly.dexPair()
      ]);
      return { router, quoter, usdc, pair };
    } catch (e) {
      console.error('Failed to get DEX addresses:', e);
      return null;
    }
  }, [subsReadOnly]);

  /**
   * Get contract details
   */
  const getContractDetails = useCallback(async (): Promise<ContractDetails | null> => {
    if (!subsReadOnly) return null;
    try {
      return await subsReadOnly.getContractDetails();
    } catch (e) {
      console.error('Failed to get contract details:', e);
      return null;
    }
  }, [subsReadOnly]);

  // ============================================================================
  // WRITE METHODS (Require signer)
  // ============================================================================

  /**
   * Create a new subscription
   */
  const subscriptionCreate = useCallback(async (
    planId: number | bigint,
    subscriber: string,
    recurring: boolean,
    remainingCycles: number,
    referral: string,
    onlyCreate: boolean,
    deductFrom: string
  ): Promise<TransactionReceipt | null> => {
    if (!subsSigner) {
      throw new Error('Wallet not connected');
    }
    try {
      const tx = await subsSigner.subscriptionCreate(
        planId,
        subscriber,
        recurring,
        remainingCycles,
        referral,
        onlyCreate,
        deductFrom
      );
      return await waitForReceipt(tx);
    } catch (e) {
      console.error('Failed to create subscription:', e);
      throw e;
    }
  }, [subsSigner, waitForReceipt]);

  /**
   * Create a new plan
   */
  const planCreate = useCallback(async (
    currency: number,
    amount: bigint,
    frequency: bigint,
    description: string,
    attribute: string,
    referralBonus: bigint
  ): Promise<{ receipt: TransactionReceipt; planId?: bigint }> => {
    if (!subsSigner || !subsReadOnly) {
      throw new Error('Wallet not connected');
    }
    try {
      // Get plan creation cost
      const fee = await subsReadOnly.planCreationCost();

      // Create plan
      const tx = await subsSigner.planCreate(
        currency,
        amount,
        frequency,
        description,
        attribute,
        referralBonus,
        { value: fee }
      );

      const receipt = await waitForReceipt(tx);

      // Try to extract plan ID from events
      let planId: bigint | undefined;
      for (const log of receipt.logs) {
        try {
          const parsed = subsSigner.interface.parseLog({
            topics: log.topics as string[],
            data: log.data
          });
          if (parsed?.name === '_planCreate' || parsed?.name === 'PlanCreated') {
            planId = parsed.args[0] as bigint;
            break;
          }
        } catch {
          // Not our event
        }
      }

      return { receipt, planId };
    } catch (e) {
      console.error('Failed to create plan:', e);
      throw e;
    }
  }, [subsSigner, subsReadOnly, waitForReceipt]);

  /**
   * Pay subscription with USDC (uses Permit2 + swap)
   */
  const paySubscriptionWithUsdc = useCallback(async (
    subscriptionId: bigint,
    feeTier: number,
    deadline: bigint,
    nonce: bigint,
    permitDeadline: bigint,
    signature: string,
    bufferBps: number
  ): Promise<TransactionReceipt | null> => {
    if (!subsSigner) {
      throw new Error('Wallet not connected');
    }
    try {
      const tx = await subsSigner.paySubscriptionWithUsdc(
        subscriptionId,
        feeTier,
        deadline,
        nonce,
        permitDeadline,
        signature,
        bufferBps
      );
      return await waitForReceipt(tx);
    } catch (e) {
      console.error('Failed to pay subscription with USDC:', e);
      throw e;
    }
  }, [subsSigner, waitForReceipt]);

  /**
   * Approve token spending
   */
  const approve = useCallback(async (spender: string, amount: bigint): Promise<TransactionReceipt | null> => {
    if (!subsSigner) {
      throw new Error('Wallet not connected');
    }
    try {
      const tx = await subsSigner.approve(spender, amount);
      return await waitForReceipt(tx);
    } catch (e) {
      console.error('Failed to approve:', e);
      throw e;
    }
  }, [subsSigner, waitForReceipt]);

  /**
   * Transfer tokens
   */
  const transfer = useCallback(async (to: string, amount: bigint): Promise<TransactionReceipt | null> => {
    if (!subsSigner) {
      throw new Error('Wallet not connected');
    }
    try {
      const tx = await subsSigner.transfer(to, amount);
      return await waitForReceipt(tx);
    } catch (e) {
      console.error('Failed to transfer:', e);
      throw e;
    }
  }, [subsSigner, waitForReceipt]);

  /**
   * Change subscription recurring settings
   */
  const subscriptionRecurringCHG = useCallback(async (
    subscriptionId: bigint,
    enabled: boolean,
    setCycles: number
  ): Promise<TransactionReceipt | null> => {
    if (!subsSigner) {
      throw new Error('Wallet not connected');
    }
    try {
      const tx = await subsSigner.subscriptionRecurringCHG(subscriptionId, enabled, setCycles);
      return await waitForReceipt(tx);
    } catch (e) {
      console.error('Failed to change recurring:', e);
      throw e;
    }
  }, [subsSigner, waitForReceipt]);

  /**
   * Quote USDC amount to SUBS amount using Uniswap V3 Quoter.
   * Centralized logic to replace potentially failing contract conversion.
   *
   * @param amountUsdcWad Amount in USDC (18 decimals)
   * @returns Amount in SUBS (18 decimals)
   */
  const quoteUsdcToSubs = useCallback(async (amountUsdcWad: bigint): Promise<bigint> => {
    if (!subsReadOnly) return 0n;

    try {
      // 1. Get DEX addresses
      const quoter = await subsReadOnly.dexQuoter();
      const usdc = await subsReadOnly.dexUSDC();

      if (!quoter || !usdc) return 0n;

      // 2. Adjust decimals (18 -> 6)
      // USDC on Arbitrum is 6 decimals. Plan amount is 18 decimals.
      const amountUsdc6 = amountUsdcWad / 1000000000000n;
      if (amountUsdc6 === 0n) return 0n;

      // 3. Get Quoter contract
      const quoterAbi = getAbi('dexQuoterABI');
      const quoterContract = getContract(quoter, quoterAbi, 'readOnly');

      // 4. Call quote
      // Try common fee tiers: 500 (0.05%), 3000 (0.3%), 10000 (1%)
      const feeTiers = [3000, 10000, 500];
      
      for (const fee of feeTiers) {
        try {
          // quoteExactInputSingle(tokenIn, tokenOut, fee, amountIn, sqrtPriceLimitX96)
          const amountOut = await quoterContract.quoteExactInputSingle.staticCall(
            usdc,
            contractAddress,
            fee,
            amountUsdc6,
            0
          );
          if (amountOut > 0n) return amountOut;
        } catch (e) {
          // Continue to next fee tier
        }
      }
    } catch (e) {
      console.error('Failed to quote USDC to SUBS:', e);
    }
    return 0n;
  }, [subsReadOnly, getAbi, getContract, contractAddress]);

  // ============================================================================
  // Utility Methods
  // ============================================================================

  /**
   * Format token amount (18 decimals)
   * Returns '0' for null/undefined values to prevent crashes when RPC fails
   */
  const formatTokenAmount = useCallback((amount: bigint | null | undefined, decimals: number = 18): string => {
    if (amount === null || amount === undefined) return '0';
    return formatUnits(amount, decimals);
  }, []);

  /**
   * Parse token amount (18 decimals)
   */
  const parseTokenAmount = useCallback((amount: string, decimals: number = 18): bigint => {
    return parseUnits(amount, decimals);
  }, []);

  /**
   * Format duration in human-readable form
   */
  const formatDuration = useCallback((seconds: bigint): string => {
    const sec = Number(seconds);
    if (sec >= 31536000) return `${Math.floor(sec / 31536000)} year(s)`;
    if (sec >= 2592000) return `${Math.floor(sec / 2592000)} month(s)`;
    if (sec >= 604800) return `${Math.floor(sec / 604800)} week(s)`;
    if (sec >= 86400) return `${Math.floor(sec / 86400)} day(s)`;
    if (sec >= 3600) return `${Math.floor(sec / 3600)} hour(s)`;
    return `${sec} second(s)`;
  }, []);

  // ============================================================================
  // Return
  // ============================================================================

  return {
    // Contract instances
    contract: subsContract,
    contractReadOnly: subsReadOnly,
    contractSigner: subsSigner,

    // State
    isReady: !!subsReadOnly,
    canWrite: !!subsSigner && isConnected,

    // Read methods
    getPlan,
    getPlans,
    getSubscription,
    getPlanSubscription,
    balanceOf,
    allowance,
    planCreationCost,
    convertTokenToOtherCurrency,
    getDexAddresses,
    getContractDetails,

    // Write methods
    subscriptionCreate,
    planCreate,
    paySubscriptionWithUsdc,
    approve,
    transfer,
    subscriptionRecurringCHG,
    quoteUsdcToSubs,

    // Utilities
    formatTokenAmount,
    parseTokenAmount,
    formatDuration,
    waitForReceipt
  };
};

export default useSubscryptsContract;
